import { UseFormReturn, Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { format } from "date-fns";
import {
  Paper,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Button,
  FormHelperText,
  InputAdornment,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Upload, CloudUpload } from "@mui/icons-material";
import styles from "./index.module.scss";
import { yupResolver } from "@hookform/resolvers/yup";
// import { currencyOptions, getCurrencySymbol } from '../../../utils/programUtils';
import { Calendar } from "lucide-react";
import { DateRange } from "rsuite/esm/DateRangePicker";
import DateRangePicker from "rsuite/DateRangePicker";
import { getItemInLocalStorage } from "../../../services/localStorage";
import { useEffect, useState } from "react";
import CustomTimer from "../../../common/components/CustomTimer";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import CustomTimePicker from "../../../common/components/CustomTimePicker";
import CustomDropDown from "../../../common/components/CustomDropDown";
import CustomVenueModal from "../../../common/components/CustomVenueModal";
import CustomDatePicker from "../../../common/components/CustomDatePicker";
import { checkToDisable } from "../../../utils/checkToDisable";
import { width } from "@mui/system";

const programDetailsSchema = yup.object().shape({
  programName: yup.string().required("Program name is required"),
  description: yup
    .string()
    .min(10, "Description should be at least 10 characters"),
  startDate: yup.date().required("Start date is required"),
  programBanner: yup.mixed().optional(),
  endDate: yup.date().required("End date is required"),
  modeOfProgram: yup
    .mixed()
    .oneOf(["online", "offline", "hybrid"])
    .required("Please select a mode"),
  venueAddress: yup.array().of(yup.string()).required("venue is not selected"),
  customVenue: yup.string().optional(),
  isTravelRequired: yup.mixed().oneOf(["yes", "no"]).optional(),
  isResidential: yup.mixed().oneOf(["yes", "no"]).optional(),
  isPaymentRequired: yup.mixed().oneOf(["yes", "no"]).required(),
  currency: yup.string().optional(),
  programFee: yup.string().optional(),
  registrationStartDate: yup
    .date()
    .required("Registration start date is required"),
  registrationStartTime: yup
    .date()
    .required("Registration start time is required"),
  registrationEndDate: yup.date().required("Registration end date is required"),
  registrationEndTime: yup.date().required("Registration end time is required"),
  approvalRequired: yup.mixed().oneOf(["yes", "no"]).required(),
  hasSeatLimit: yup.mixed().oneOf(["yes", "no"]),
  seatLimit: yup.string().optional(),
  hasWaitlist: yup.mixed().oneOf(["yes", "no"]).required(),
  launchStartDate: yup.date().optional(),
  launchstartTime: yup.date().optional(),
  waitlistTriggerCount: yup.string().optional(),
  subPrograms: yup.array().of(yup.mixed()).required(),
});

interface ProgramDetailsFormProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors;
  getValues: UseFormGetValues<any>;
  trigger: UseFormTrigger<any>;
  programType: any;
  uploadedBanner: File | null;
  bannerImageUrl: string | null;
  showCustomVenue: boolean;
  onBannerUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onVenueChange: (value: string[]) => void;
  getCurrencySymbol: (currency: string) => string;
  currencyOptions: Array<{ value: string; label: string; symbol: string }>;
  venueOptions: string[];
  dateRange: [Date | null, Date | null];
  setDateRange: (range: [Date | null, Date | null]) => void;
  onDateChange: (startDate: Date | null, endDate: Date | null) => void;
  registrationDateRange: [Date | null, Date | null];
  setRegistrationDateRange: (range: [Date | null, Date | null]) => void;
  onRegistrationDateChange: (
    startDate: Date | null,
    endDate: Date | null,
  ) => void;
  programTypeNameFromSource?: string;
}

type ImageData = {
  dataUrl: string | null;
  name: string | null;
};
const ProgramDetailsForm = ({
  control,
  setValue,
  watch,
  errors,
  getValues,
  trigger,
  programType,
  uploadedBanner,
  bannerImageUrl,
  showCustomVenue,
  onBannerUpload,
  onVenueChange,
  getCurrencySymbol,
  currencyOptions,
  venueOptions,
  dateRange,
  setDateRange,
  onDateChange,
  registrationDateRange,
  setRegistrationDateRange,
  onRegistrationDateChange,
  programTypeNameFromSource,
}: ProgramDetailsFormProps) => {
  // Watch for changes
  const modeOfProgram = watch("modeOfProgram");
  const isPaymentRequired = watch("isPaymentRequired");
  const hasSeatLimit = watch("hasSeatLimit");
  const hasWaitlist = watch("hasWaitlist");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const registrationStartDate = watch("registrationStartDate");
  const registrationEndDate = watch("registrationEndDate");
  const selectedCurrency = watch("currency");
  useEffect(() => {
    const venueAddress = watch("venueAddress");
    if (venueAddress && venueAddress.length > 0) {
      setValue("venueNameInEmail", venueAddress[0]);
    } else {
      setValue("venueNameInEmail", "");
    }
  }, [watch("venueAddress")]);
  // Handle program date range change
  const handleDateRangeChange = (value: DateRange | null) => {
    if (value && value.length === 2) {
      const [startDate, endDate] = value;

      // Update local state
      if (setDateRange) {
        setDateRange([startDate, endDate]);
      }

      // Update form values
      setValue("startDate", startDate);
      setValue("endDate", endDate);

      // Pass to parent component
      if (onDateChange) {
        onDateChange(startDate, endDate);
      }
    }
  };
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  useEffect(() => {
    setUploadedFile(uploadedBanner);
  }, [uploadedBanner, setUploadedFile]);

  // Handle registration date range change
  const handleRegistrationDateRangeChange = (value: DateRange | null) => {
    if (value && value.length === 2) {
      const [regStartDate, regEndDate] = value;

      // Update local state
      if (setRegistrationDateRange) {
        setRegistrationDateRange([regStartDate, regEndDate]);
      }

      // Update form values
      setValue("registrationStartDate", regStartDate);
      setValue("registrationEndDate", regEndDate);

      // Pass to parent component
      if (onRegistrationDateChange) {
        onRegistrationDateChange(regStartDate, regEndDate);
      }
    }
  };

  // Clear program dates function
  const clearDates = () => {
    if (setDateRange) {
      setDateRange([null, null]);
    }
    setValue("startDate", null);
    setValue("endDate", null);
    if (onDateChange) {
      onDateChange(null, null);
    }
  };

  // Clear registration dates function
  const clearRegistrationDates = () => {
    if (setRegistrationDateRange) {
      setRegistrationDateRange([null, null]);
    }
    setValue("registrationStartDate", null);
    setValue("registrationEndDate", null);
    if (onRegistrationDateChange) {
      onRegistrationDateChange(null, null);
    }
  };

  // Format date range for display - Program dates
  const getProgramDateRangeValue = (): DateRange | null => {
    // First check the form values
    const formStartDate = watch("startDate");
    const formEndDate = watch("endDate");

    if (formStartDate && formEndDate) {
      return [new Date(formStartDate), new Date(formEndDate)];
    }

    // Fallback to dateRange prop
    if (dateRange && dateRange[0] && dateRange[1]) {
      return [new Date(dateRange[0]), new Date(dateRange[1])];
    }

    return null;
  };

  // Format date range for display - Registration dates
  const getRegistrationDateRangeValue = (): DateRange | null => {
    // First check the form values
    const formRegStartDate = watch("registrationStartDate");
    const formRegEndDate = watch("registrationEndDate");

    if (formRegStartDate && formRegEndDate) {
      return [new Date(formRegStartDate), new Date(formRegEndDate)];
    }

    // Fallback to registrationDateRange prop
    if (
      registrationDateRange &&
      registrationDateRange[0] &&
      registrationDateRange[1]
    ) {
      return [
        new Date(registrationDateRange[0]),
        new Date(registrationDateRange[1]),
      ];
    }

    return null;
  };
  const seekerDetails = getItemInLocalStorage("seekerDetails");
  const [isCustomVenueModalOpen, setCustomVenueModalOpen] = useState(false);

  const handleVenueChange = (value) => {
    if (value === "Add Custom Venue") {
      setCustomVenueModalOpen(true);
    } else {
      setValue("venueAddress", [value]);
    }
  };

  const handleAddCustomVenue = (venue) => {
    setValue("venueAddress", [venue]);
    setCustomVenueModalOpen(false);
  };

  return (
    <>
      <Controller
        name="registrationStartDate"
        control={control}
        render={() => null}
      />
      <Controller
        name="registrationEndDate"
        control={control}
        render={() => null}
      />

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className={styles.formContainer}>
          {/* Header */}

          {/* Form Content */}
          <div className={styles.formContent}>
            <p className={styles.Detailstitle}>Program details</p>
            <p className={styles.subtitle}>
              We'll start with the program details — Give it a name, describe
              its essence, and set the foundation.
            </p>

            {/* Mode of Program */}
            <div className={styles.formSection}>
              <div className={styles.fieldGroup}>
                <div>
                  <label className={styles.fieldLabel}>
                    What's the program called?
                  </label>
                  <Controller
                    name="programName"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder="HDB -25"
                        className={`${styles.inputFieldtitle} ${
                          errors.programName ? styles.inputError : ""
                        }`}
                      />
                    )}
                  />
                  {errors.programName && (
                    <span className={styles.errorText}>
                      {errors.programName.message}
                    </span>
                  )}
                </div>
              </div>
              {/* Program Description */}
              <div className={styles.formSection}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Add description of the program
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        placeholder="What is this program all about? Share its core intention and unique value."
                        className={`${styles.textareaField} ${
                          errors.description ? styles.inputError : ""
                        }`}
                        rows={4}
                      />
                    )}
                  />
                  {errors.description && (
                    <span className={styles.errorText}>
                      {errors.description.message}
                    </span>
                  )}
                </div>
              </div>
              {/* Mode of Program */}
              <div className={styles.formSection}>
                <div className={styles.fieldGroupMode}>
                  <div className={styles.venueField}>
                    <label className={styles.fieldLabel}>
                      What is mode of program?
                    </label>
                    <div className={styles.radioGroup}>
                      <Controller
                        name="modeOfProgram"
                        control={control}
                        render={({ field }) => (
                          <>
                            <label className={styles.radioOption}>
                              <input
                                type="radio"
                                {...field}
                                value="online"
                                checked={field.value === "online"}
                                className={styles.radioInput}
                                disabled={checkToDisable(
                                  programTypeNameFromSource,
                                  "modeOfProgram-online",
                                )}
                              />
                              <span className={styles.radioLabel}>Online</span>
                            </label>
                            <label className={styles.radioOption}>
                              <input
                                type="radio"
                                {...field}
                                value="offline"
                                checked={field.value === "offline"}
                                className={styles.radioInput}
                              />
                              <span className={styles.radioLabel}>Offline</span>
                            </label>
                            <label className={styles.radioOption}>
                              <input
                                type="radio"
                                {...field}
                                value="hybrid"
                                checked={field.value === "hybrid"}
                                className={styles.radioInput}
                                disabled={checkToDisable(
                                  programTypeNameFromSource,
                                  "modeOfProgram-hybrid",
                                )}
                              />
                              <span className={styles.radioLabel}>Hybrid</span>
                            </label>
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {watch("modeOfProgram") === "offline" && (
                <div className={styles.formSection}>
                  <div
                    className={styles.fieldGroupMode}
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      alignContent: "center",
                      alignSelf: "center",
                    }}
                  >
                    {/* Is this residential program? */}
                    <div className={styles.venueField}>
                      <label className={styles.fieldLabel}>
                        Is this residential program?
                      </label>
                      <div className={styles.radioGroup}>
                        <Controller
                          name="isResendential"
                          control={control}
                          render={({ field }) => (
                            <>
                              <label className={styles.radioOption}>
                                <input
                                  type="radio"
                                  {...field}
                                  value="yes"
                                  checked={field.value === "yes"}
                                  className={styles.radioInput}
                                  onChange={() => field.onChange("yes")}
                                />
                                <span className={styles.radioLabel}>Yes</span>
                              </label>
                              <label className={styles.radioOption}>
                                <input
                                  type="radio"
                                  {...field}
                                  value="no"
                                  checked={field.value === "no"}
                                  className={styles.radioInput}
                                  onChange={() => field.onChange("no")}
                                  disabled={checkToDisable(
                                    programTypeNameFromSource,
                                    "isResendential-no",
                                  )}
                                />
                                <span className={styles.radioLabel}>No</span>
                              </label>
                            </>
                          )}
                        />
                      </div>
                    </div>
                    {/* Venue Selection */}
                    {watch("isResendential") === "yes" && (
                      <div className={styles.venueField}>
                        <label className={styles.fieldLabel}>
                          Where will the program be held?
                        </label>
                        <Controller
                          name="venueAddress"
                          control={control}
                          render={({ field }) => (
                            <CustomDropDown
                              value={
                                watch("venueAddress") &&
                                watch("venueAddress").length > 0
                                  ? venueOptions.find((venue) =>
                                      watch("venueAddress")[0]
                                        ?.toLowerCase()
                                        .includes(venue.toLowerCase()),
                                    ) || ""
                                  : ""
                              }
                              onChange={handleVenueChange}
                              options={
                                venueOptions && venueOptions.length > 0
                                  ? venueOptions.map((venue) => ({
                                      value: venue,
                                      label:
                                        venue.charAt(0).toUpperCase() +
                                        venue.slice(1),
                                    }))
                                  : [
                                      {
                                        value: "",
                                        label: "Other",
                                      },
                                    ]
                              }
                              placeholder="Select the venue"
                              width={340}
                              height={40}
                              error={
                                !!(
                                  errors.venueAddress &&
                                  (!field.value || field.value.length === 0)
                                )
                              }
                              className={styles.selectFieldFixed}
                            />
                          )}
                        />
                        {errors.venueAddress &&
                          (!watch("venueAddress") ||
                            watch("venueAddress").length === 0) && (
                            <span
                              className={styles.errorText}
                              style={{
                                color: "#e28619",
                                fontSize: "13=2px",
                                marginTop: "4px",
                                display: "block",
                                position: "static",
                                background: "#fff",
                                padding: "0 8px",
                                borderRadius: "4px",
                                textAlign: "right",
                              }}
                            >
                              <p>venue is required</p>
                            </span>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!watch("programName")?.includes("HDB/MSD") && (
                // <div className={styles.formSection}>
                //   <div className={styles.fieldGroupMode}>
                <div className={styles.fieldGroupMode}>
                  <div className={styles.seatLimit}>
                    <label className={styles.fieldLabel}>
                      Is there a limit on participants?
                    </label>
                    <div className={styles.radioGroup}>
                      <Controller
                        name="hasSeatLimit"
                        control={control}
                        render={({ field }) => (
                          <>
                            <label className={styles.radioOption}>
                              <input
                                type="radio"
                                {...field}
                                value="yes"
                                checked={field.value === "yes"}
                                className={styles.radioInput}
                              />
                              <span className={styles.radioLabel}>
                                Limit participants
                              </span>
                            </label>
                            <label className={styles.radioOption}>
                              <input
                                type="radio"
                                {...field}
                                value="no"
                                checked={field.value === "no"}
                                className={styles.radioInput}
                              />
                              <span className={styles.radioLabel}>
                                Unlimited participants
                              </span>
                            </label>
                          </>
                        )}
                      />
                    </div>
                    {errors.hasSeatLimit && (
                      <span className={styles.errorText}>
                        {errors.hasSeatLimit.message}
                      </span>
                    )}
                  </div>
                  {hasSeatLimit === "yes" && (
                    <div className={styles.seatField}>
                      <label className={styles.fieldLabel}>
                        Number of seats
                      </label>
                      <Controller
                        name="seatLimit"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="number"
                            placeholder="Enter Seats"
                            className={styles.inputField}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
                //   </div>
                // </div>
              )}
              {!watch("modeOfProgram")?.includes("HDB/MSD") && (
                <div className={styles.formSection}>
                  <div className={styles.fieldGroupMode}>
                    <div className={styles.venueField}>
                      <label className={styles.fieldLabel}>
                        Enable waitlist when full?
                      </label>
                      <div className={styles.radioGroup}>
                        <Controller
                          name="hasWaitlist"
                          control={control}
                          defaultValue="yes"
                          render={({ field }) => (
                            <>
                              <label className={styles.radioOption}>
                                <input
                                  type="radio"
                                  {...field}
                                  value="yes"
                                  checked={
                                    field.value === "yes" ||
                                    field.value === undefined
                                  }
                                  className={styles.radioInput}
                                />
                                <span className={styles.radioLabel}>
                                  Yes, allow waitlist
                                </span>
                              </label>
                              <label className={styles.radioOption}>
                                <input
                                  type="radio"
                                  {...field}
                                  value="no"
                                  checked={field.value === "no"}
                                  className={styles.radioInput}
                                />
                                <span className={styles.radioLabel}>
                                  No Waitlist
                                </span>
                              </label>
                            </>
                          )}
                        />
                      </div>
                      {errors.hasWaitlist && (
                        <span className={styles.errorText}>
                          {errors.hasWaitlist.message}
                        </span>
                      )}
                    </div>

                    {hasWaitlist === "yes" && (
                      <div className={styles.waitlistField}>
                        <label className={styles.fieldLabel}>
                          Trigger count
                        </label>
                        <Controller
                          name="waitlistTriggerCount"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="number"
                              placeholder="Enter Count"
                              className={styles.inputField}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Program Dates */}
              <div className={styles.formSection}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Program start and end dates
                  </label>
                  <div className={styles.dateRow}>
                    <div className={styles.dateField}>
                      {/* <span className={styles.dateLabel}>
                        Setting end date of the program by default
                      </span> */}

                      <div className={styles.datePicker}>
                        <DateRangePicker
                          placement="bottomStart"
                          value={getProgramDateRangeValue()}
                          onOk={handleDateRangeChange}
                          onChange={(value) => {
                            handleDateRangeChange(value);
                            trigger("startDate");
                            trigger("endDate");
                          }}
                          disabledDate={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                          }}
                          ranges={[]}
                          format="dd MMM yyyy"
                          onClean={clearDates}
                          placeholder="Select program date range"
                          style={{ width: "105%" }}
                          className={
                            errors.startDate || errors.endDate
                              ? styles.datePickerError
                              : styles.datePicker
                          }
                          onBlur={() => {
                            trigger("startDate");
                            trigger("endDate");
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Display validation errors for dates */}
                  <div className={styles.dateErrorRow}>
                    {errors.startDate && (
                      <span className={styles.errorText}>
                        {errors.startDate.message}
                      </span>
                    )}
                    {errors.endDate && (
                      <span className={styles.errorText}>
                        {errors.endDate.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={styles.RegistrationDiv}
                  data-testid="register-div"
                >
                  <div
                    className={styles.RegistrationInputContainer}
                    data-testid="register-date-div"
                  >
                    <p
                      className={styles.labelText}
                      data-testid="label-text-start-date"
                    >
                      Registration start date & time
                    </p>
                    <div
                      className={styles.containerDateTime}
                      data-testid="program-start-date-time-inputs"
                    >
                      <div
                        className={styles.containerDate}
                        data-testid="form-fields-registrations"
                      >
                        <Controller
                          name={`registrationStartDate`}
                          control={control}
                          render={({ field }) => (
                            <CustomDatePicker
                              value={field.value}
                              futureDate={true}
                              onChange={(date) => {
                                field.onChange(date);
                                trigger(`registrationStartDate`);
                                trigger(`registrationStartTime`);
                              }}
                              maxDate={watch("startDate") || null}
                              startDate={new Date()}
                              borderRight={true}
                              errorExist={
                                !!errors?.registrationStartDate ||
                                !!errors?.registrationStartTime
                              }
                              errorMessage={
                                errors?.registrationStartDate?.message
                              }
                              data-testid="custom-date-picker"
                              dataTestId="program-start-date"
                            />
                          )}
                        />
                      </div>
                      <div
                        className={styles.containerTime}
                        data-testid="form-fields"
                      >
                        <Controller
                          name={`registrationStartTime`}
                          control={control}
                          render={({ field }) => (
                            <CustomTimePicker
                              value={field.value}
                              errorExist={
                                !!errors?.registrationStartDate ||
                                !!errors?.registrationStartTime
                              }
                              onChange={(time: Date | null) => {
                                if (time && !isNaN(time.getTime())) {
                                  setValue(`registrationStartTime`, time);
                                }
                                trigger(`registrationStartTime`);
                                trigger(`registrationStartDate`);
                              }}
                              data-testid="custom-time-picker"
                              dataTestid="program-start-time"
                            />
                          )}
                        />
                      </div>
                    </div>
                    {errors?.registrationStartDate && (
                      <i
                        className={styles.errorMsg}
                        data-testid="error-msg-start-date"
                      >
                        {errors.registrationStartDate?.message}
                      </i>
                    )}
                    {errors?.registrationStartTime &&
                      !errors?.registrationStartTime && (
                        <i
                          className={styles.errorMsg}
                          data-testid="error-msg-start-time"
                        >
                          {errors.registrationStartTime?.message}
                        </i>
                      )}
                  </div>
                </div>
              </div>
              <div className={styles.formSection}>
                <div className={styles.RegistrationDiv} data-testid="end-div">
                  <div
                    className={styles.RegistrationInputContainer}
                    data-testid="end-date-div"
                  >
                    <p
                      className={styles.labelText}
                      data-testid="label-text-end-date"
                    >
                      Registration end date & time
                    </p>
                    <div
                      className={styles.containerDateTime}
                      data-testid="program-end-date-time-inputs"
                    >
                      <div
                        className={styles.containerDate}
                        data-testid="form-fields-end"
                      >
                        <Controller
                          name="registrationEndDate"
                          control={control}
                          render={({ field }) => (
                            <CustomDatePicker
                              value={field.value}
                              futureDate={true}
                              onChange={(date) => {
                                field.onChange(date);
                                trigger(`registrationEndDate`);
                                trigger(`registrationEndTime`);
                              }}
                              // maxDate={watch("startDate") || null}
                              minDate={watch("registrationStartDate") || null}
                              startDate={new Date()}
                              borderRight={true}
                              errorExist={
                                !!errors?.RegistrationEndDate ||
                                !!errors?.RegistrationEndTime
                              }
                              errorMessage={
                                errors?.RegistrationEndDate?.message
                              }
                              data-testid="custom-date-picker"
                              dataTestId="program-end-date"
                            />
                          )}
                        />
                      </div>
                      <div
                        className={styles.containerTime}
                        data-testid="form-fields"
                      >
                        <Controller
                          name="registrationEndTime"
                          control={control}
                          render={({ field }) => (
                            <CustomTimePicker
                              value={field.value}
                              errorExist={
                                !!errors?.registrationEndDate ||
                                !!errors?.registrationEndTime
                              }
                              onChange={(time: Date | null) => {
                                if (time && !isNaN(time.getTime())) {
                                  setValue(`registrationEndTime`, time);
                                }
                                trigger(`registrationEndTime`);
                                trigger(`registrationEndDate`);
                              }}
                              data-testid="custom-time-picker"
                              dataTestid="program-end-time"
                            />
                          )}
                        />
                      </div>
                    </div>
                    {errors?.registrationEndDate && (
                      <i
                        className={styles.errorMsg}
                        data-testid="error-msg-end-date"
                      >
                        {errors.registrationEndDate?.message}
                      </i>
                    )}
                    {errors?.registrationEndTime &&
                      !errors?.registrationEndDate && (
                        <i
                          className={styles.errorMsg}
                          data-testid="error-msg-end-time"
                        >
                          {errors.registrationEndTime?.message}
                        </i>
                      )}
                  </div>
                </div>
              </div>
              {/* Payment Section */}
              {/* Participants Limit */}
              {/* Waitlist */}
              {/* Approval Required */}
              <div className={styles.formSection}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Do you need to approve Registration?
                  </label>
                  <div className={styles.radioGroup}>
                    <Controller
                      name="approvalRequired"
                      control={control}
                      render={({ field }) => (
                        <>
                          <label className={styles.radioOption}>
                            <input
                              type="radio"
                              {...field}
                              value="yes"
                              checked={field.value === "yes"}
                              className={styles.radioInput}
                            />
                            <span className={styles.radioLabel}>
                              Yes, require approval
                            </span>
                          </label>
                          <label className={styles.radioOption}>
                            <input
                              type="radio"
                              {...field}
                              value="no"
                              checked={field.value === "no"}
                              className={styles.radioInput}
                              disabled={checkToDisable(
                                programTypeNameFromSource,
                                "approvalRequired-no",
                              )}
                            />
                            <span className={styles.radioLabel}>
                              No approval needed
                            </span>
                          </label>
                        </>
                      )}
                    />
                  </div>
                  {errors.approvalRequired && (
                    <span className={styles.errorText}>
                      {errors.approvalRequired.message}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.formSection}>
                <div className={styles.fieldGroupModeTwo}>
                  {/* <div className={styles.venueField}> */}
                  {/* <label className={styles.fieldLabel}>
                  Is there a fee for the program?
                </label>
                <div className={styles.radioGroup}>
                  <Controller
                    name="isPaymentRequired"
                    control={control}
                    render={({ field }) => (
                      <>
                        <label className={styles.radioOption}>
                          <input
                            type="radio"
                            {...field}
                            value="yes"
                            checked={field.value === "yes"}
                            className={styles.radioInput}
                          />
                          <span className={styles.radioLabel}>Yes</span>
                        </label>
                        <label className={styles.radioOption}>
                          <input
                            type="radio"
                            {...field}
                            value="no"
                            checked={field.value === "no"}
                            className={styles.radioInput}
                          />
                          <span className={styles.radioLabel}>No</span>
                        </label>
                      </>
                    )}
                  />
                </div>
                {errors.isPaymentRequired && (
                  <span className={styles.errorText}>
                    {errors.isPaymentRequired.message}
                  </span>
                )} */}

                  <div
                    className={
                      programTypeNameFromSource?.includes("HDB")
                        ? `${styles.feeSection}`
                        : `${styles.feeSectionHDB}`
                    }
                  >
                    <div className={styles.feeDisableField}>
                      <label className={styles.fieldLabelText}>
                        Select the currency for the program fee
                      </label>
                      {/* <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className={styles.selectField}>
                        <option value="">Select the Currency</option>
                        {currencyOptions.map((currency) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.label}
                          </option>
                        ))}
                      </select>
                    )}
                  /> */}

                      <Controller
                        name="currency"
                        control={control}
                        render={({ field }) => (
                          <div className={styles.venueFfeeField}>
                            {/* <label className={styles.fieldLabel}>
                            Currency for the sub-program fee
                          </label> */}
                            <input
                              {...field}
                              type="text"
                              value="INR (₹)"
                              readOnly
                              className={styles.INRField}
                            />
                          </div>
                        )}
                      />
                    </div>
                    {/* Fee inputs in the next row, all in a flex row */}

                    {programTypeNameFromSource?.includes("HDB") ? (
                      <div className={styles.subProgramFeeSection}>
                        <div className={styles.feeRow}>
                          <div className={styles.feeField}>
                            <label className={styles.inlineLabel}>
                              Enter HDB Fee
                            </label>
                            <div className={styles.inputWithSymbol}>
                              <Controller
                                name="hdbFee"
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="number"
                                    placeholder="Enter Fee"
                                    className={`${styles.inputField} ${errors.hdbFee ? styles.inputError : ""}`}
                                  />
                                )}
                              />
                              <span className={styles.currencySymbol}>₹</span>
                            </div>
                            {errors.hdbFee && (
                              <span className={styles.errorText}>
                                {errors.hdbFee.message}
                              </span>
                            )}
                          </div>

                          <div className={styles.feeField}>
                            <label className={styles.inlineLabel}>
                              Enter MSD Fee
                            </label>
                            <div className={styles.inputWithSymbol}>
                              <Controller
                                name="msdFee"
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="number"
                                    placeholder="Enter Fee"
                                    className={`${styles.inputField} ${errors.msdFee ? styles.inputError : ""}`}
                                  />
                                )}
                              />
                              <span className={styles.currencySymbol}>₹</span>
                            </div>
                            {errors.msdFee && (
                              <span className={styles.errorText}>
                                {errors.msdFee.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.subProgramFeeSection}>
                        <div className={styles.feeRow}>
                          <div className={styles.feeField}>
                            <label className={styles.fieldLabel}>
                              Enter program Fee
                            </label>
                            <div className={styles.currencyInput}>
                              <span className={styles.currencySymbol}>
                                {getCurrencySymbol(selectedCurrency || "INR")}
                              </span>
                              <Controller
                                name="programFee"
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="number"
                                    placeholder="Enter Fee"
                                    className={styles.inputField}
                                    onBlur={() => {
                                      trigger("programFee");
                                    }}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* </div> */}
                  </div>
                </div>
              </div>
              <div className={styles.contactFormSection}>
                <div className={styles.ContactfieldGroup}>
                  <div className={styles.contactField}>
                    <label className={styles.fieldLabel}>
                      Help line number
                    </label>
                    <Controller
                      name="helpLineNumber"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          placeholder="1234567890"
                          className={`${styles.inputFieldtitle} ${
                            errors.helpLineNumber ? styles.inputError : ""
                          }`}
                        />
                      )}
                    />
                    {errors.helpLineNumber && (
                      <span className={styles.contactErrorText}>
                        {errors.helpLineNumber.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.contactField}>
                    <label className={styles.fieldLabel}>
                      Name of the emails sender
                    </label>
                    <Controller
                      name="emailSenderName"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          placeholder="infinitheism"
                          className={`${styles.inputFieldtitle} ${
                            errors.emailSenderName ? styles.inputError : ""
                          }`}
                        />
                      )}
                    />
                    {errors.emailSenderName && (
                      <span className={styles.contactErrorText}>
                        {errors.emailSenderName.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.contactField}>
                  <label className={styles.fieldLabel}>
                    Name of the venue in email
                  </label>
                  <Controller
                    name="venueNameInEmail"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder="Leonia Holistic Destination"
                        className={`${styles.inputFieldtitle} ${
                          errors.venueNameInEmail ? styles.inputError : ""
                        }`}
                      />
                    )}
                  />
                  {errors.venueNameInEmail && (
                    <span className={styles.contactErrorText}>
                      {errors.venueNameInEmail.message}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.formSection}>
                <div className={styles.fieldGroup}>
                  <div
                    className={styles.formRowContainerRegistrations}
                    data-testid="form-row-container-registrations"
                  >
                    <div className={styles.launchDiv} data-testid="launch-div">
                      <div
                        className={styles.launchDateDiv}
                        data-testid="launch-date-div"
                      >
                        <p
                          className={styles.labelText}
                          data-testid="label-text-start-date"
                        >
                          Launch date & time
                        </p>
                        <div className={styles.formLaunchDateContainer}>
                          <div
                            className={styles.formLaunchDate}
                            data-testid="form-fields-launch"
                          >
                            <Controller
                              name="launchStartDate"
                              control={control}
                              render={({ field }) => (
                                <CustomDatePicker
                                  value={field.value}
                                  futureDate={true}
                                  maxDate={(watch("startDate") as Date) || null}
                                  onChange={(date: any) => {
                                    field.onChange(date);
                                    trigger("launchStartDate");
                                    trigger("launchStartTime");
                                  }}
                                  startDate={new Date()}
                                  borderRight={true}
                                  errorExist={
                                    !!errors.launchStartDate ||
                                    !!errors.launchStartTime
                                  }
                                  errorMessage={errors.launchStartDate?.message}
                                  data-testid="custom-date-picker"
                                  dataTestId="start-date"
                                />
                              )}
                            />
                          </div>
                          <div
                            className={styles.formFields}
                            data-testid="form-fields"
                          >
                            <Controller
                              name="launchStartTime"
                              control={control}
                              render={({ field }) => (
                                <CustomTimePicker
                                  value={field.value}
                                  errorExist={
                                    !!errors.registrationStartDate ||
                                    !!errors.registrationStartTime
                                  }
                                  onChange={(time: Date | null) => {
                                    if (time && !isNaN(time.getTime())) {
                                      setValue("launchStartTime", time);
                                    }
                                    trigger("launchStartTime");
                                    trigger("launchStartDate");
                                  }}
                                  data-testid="custom-time-picker"
                                  dataTestid="start"
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      <div
                        className={styles.RegisterDateDiv}
                        data-testid="launch-time-div"
                      >
                        {/* <p
                            className={styles.labelText}
                            data-testid="label-text-start-time"
                          >
                            Start time
                          </p> */}
                      </div>
                    </div>
                    {errors.launchStartDate && (
                      <i
                        className={styles.errorMsgDate}
                        data-testid="error-msg-start-date"
                      >
                        {errors.launchStartDate?.message}
                      </i>
                    )}
                    {errors.launchStartTime && !errors.launchStartDate && (
                      <i
                        className={styles.errorMsgDate}
                        data-testid="error-msg-start-time"
                      >
                        {errors.launchStartTime?.message}
                      </i>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LocalizationProvider>
      <CustomVenueModal
        isOpen={isCustomVenueModalOpen}
        onClose={() => setCustomVenueModalOpen(false)}
        onAdd={handleAddCustomVenue}
      />
    </>
  );
};

export default ProgramDetailsForm;
